import { signal } from '@preact/signals';

/** Top-level screens. */
export type Screen = 'boot' | 'title' | 'select' | 'create' | 'loading' | 'game';

/** Full-screen/side panels opened from the HUD or hotkeys. */
export type PanelId =
  | 'character'
  | 'inventory'
  | 'skills'
  | 'quests'
  | 'map'
  | 'sigils'
  | 'cards'
  | 'journal'
  | 'settings'
  | 'temper'
  | 'shop'
  | 'bounty'
  | 'market'
  | 'haven'
  | 'bonds'
  | 'instances'
  | 'mail'
  | 'emotes'
  | 'party'
  | 'lifeskill'
  | 'waystone'
  | 'rewards'
  | 'sail'
  | 'events'
  | 'pets';

export interface DialogueState {
  npcId: string;
  name: string;
  role: string;
  lines: string[];
  /** Index of the line being shown. */
  index: number;
  options: { label: string; kind: 'quest' | 'turnin' | 'service' | 'bye' | 'bond'; id?: string }[];
}

export interface RewardsState {
  title: string;
  subtitle?: string;
  silver: number;
  gold: number;
  items: { id: string; count: number }[];
  equip: { name: string; rarity: string }[];
  cards: string[];
  xp?: number;
}

export const ui = {
  screen: signal<Screen>('boot'),
  panel: signal<PanelId | null>(null),
  /** Extra data for the open panel (e.g. which NPC opened the shop). */
  panelArg: signal<string | null>(null),
  dialogue: signal<DialogueState | null>(null),
  rewards: signal<RewardsState | null>(null),
  loadingText: signal('Loading…'),
  loadingProgress: signal(0),
  /** Bumped whenever progression data changes so panels re-render. */
  version: signal(0),
  confirm: signal<{ text: string; ok: () => void; okLabel?: string } | null>(null),
  deathOptions: signal<{ canPlume: boolean; canCheckpoint: boolean; timer: number } | null>(null),
  chatOpen: signal(false),
  /** Class shown on the 3D dais behind the menus (select / create screens). */
  showcaseClass: signal<string | null>(null),
  /** Graphics preset in use (what "Auto" picked, when the setting is Auto). */
  graphicsLevel: signal<'low' | 'medium' | 'high'>('high'),
};

export function bump(): void {
  ui.version.value++;
}

export function openPanel(id: PanelId, arg: string | null = null): void {
  if (ui.panel.value === id && ui.panelArg.value === arg) {
    ui.panel.value = null;
    return;
  }
  ui.panelArg.value = arg;
  ui.panel.value = id;
}

export function closePanel(): void {
  ui.panel.value = null;
  ui.panelArg.value = null;
}
